import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    onAuthStateChanged,
    reauthenticateWithCredential,
    EmailAuthProvider,
} from 'firebase/auth';
import type { User as FirebaseUser, Unsubscribe } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { UserService } from './user.service';

export class AuthService {
    static async register(email: string, password: string, name: string): Promise<FirebaseUser> {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const fbUser = userCredential.user;

        // Create user document in Firestore
        await UserService.createUser({
            id: fbUser.uid,
            email,
            name,
            avatar: '',
            role: 'user', // Default role
        });

        return fbUser;
    }

    static async login(email: string, password: string): Promise<FirebaseUser> {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return userCredential.user;
    }

    static async logout(): Promise<void> {
        await signOut(auth);
    }

    static async sendPasswordReset(email: string): Promise<void> {
        await sendPasswordResetEmail(auth, email);
    }

    static getCurrentUser(): FirebaseUser | null {
        return auth.currentUser;
    }

    static onAuthStateChanged(callback: (user: FirebaseUser | null) => void): Unsubscribe {
        return onAuthStateChanged(auth, callback);
    }

    static async deleteAccount(password: string): Promise<void> {
        const user = auth.currentUser;
        if (!user || !user.email) throw new Error('Not authenticated');

        // Re-authenticate
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);

        // Delete data then user
        await UserService.deleteUser(user.uid);
        await user.delete();
    }
}
